import React, { useState } from 'react';
import _ from 'lodash';
import WaiverAirlineRelationshipsModal from './WaiverAirlineRelationshipsModal';

interface Relationship {
  bookingAirlineCodes: string[];
  operatingAirlineCodes: string[];
}

interface AirlineRelationshipsProps {
  relationships: Relationship[];
  label: string;
  addButtonLabel?: string;
  onChange?: (updatedRelationships: { value: Relationship[] }) => void;
  readOnly?: boolean;
}

const AirlineRelationships: React.FC<AirlineRelationshipsProps> = ({
  relationships,
  label,
  addButtonLabel,
  onChange,
  readOnly,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentRelationships, setCurrentRelationships] = useState<Relationship[]>(relationships || []);

  const formatRelationship = (relationship: Relationship): string => {
    const hasBookingAirlineCodes =
      relationship.bookingAirlineCodes &&
      relationship.bookingAirlineCodes.length > 0;
    const hasOperatingAirlineCodes =
      relationship.operatingAirlineCodes &&
      relationship.operatingAirlineCodes.length > 0;
    const bookingAirlineCodes = hasBookingAirlineCodes
      ? `Booked on ${relationship.bookingAirlineCodes.join(' or ')}`
      : null;
    const operatingAirlineCodes = hasOperatingAirlineCodes
      ? `Operated by ${relationship.operatingAirlineCodes.join(' or ')}`
      : null;
    return [bookingAirlineCodes, operatingAirlineCodes]
      .filter(v => v)
      .join(', ');
  };

  const isEmpty = (relationship: Relationship): boolean =>
    _.isEmpty(relationship.bookingAirlineCodes) &&
    _.isEmpty(relationship.operatingAirlineCodes);

  const onSave = (modalRelationship: { bookedOn: string[]; operatedBy: string[] }) => {
    setIsOpen(false);

    const newRelationship: Relationship = {
      bookingAirlineCodes: modalRelationship.bookedOn,
      operatingAirlineCodes: modalRelationship.operatedBy,
    };

    if (!isEmpty(newRelationship)) {
      const updatedRelationships = currentRelationships
        ? [...currentRelationships, newRelationship]
        : [newRelationship];
      setCurrentRelationships(updatedRelationships);
      handleChange(updatedRelationships);
    }
  };

  const onCancel = () => {
    setIsOpen(false);
  };

  const addRelationship = () => {
    openAirlineRelationshipsModal();
  };

  const removeRelationship = (relationshipToRemove: Relationship) => {
    const updatedRelationships = currentRelationships.filter(
      filterRelationship => relationshipToRemove !== filterRelationship,
    );
    setCurrentRelationships(updatedRelationships);
    handleChange(updatedRelationships);
  };

  const handleChange = (updatedRelationships: Relationship[]) => {
    if (onChange) {
      onChange({ value: updatedRelationships });
    }
  };

  const openAirlineRelationshipsModal = () => {
    setIsOpen(true);
  };

  return (
    <div className="form-group mini-section">
      {isOpen && (
      <WaiverAirlineRelationshipsModal
        isOpen={isOpen}
        onSave={onSave}
        onCancel={onCancel}
      />
      )}
      <label>{label}:</label>
      <div className="waiver-item-list section-content">
        {currentRelationships.map((relationship, index) => (
          <div key={index} className="form-control section-item waiver-item">
            {formatRelationship(relationship)}
            {!readOnly && (
              <a onClick={() => removeRelationship(relationship)} className="remove-item">X</a>
            )}
          </div>
        ))}
        {!readOnly && (
          <button
            className="btn btn-default waiver-button action-button"
            type="button"
            onClick={addRelationship}
          >
            {addButtonLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default AirlineRelationships;